/**
 * Operation Registry
 * Central registry for all operation handlers
 * Replaces the massive switch statement in applyOperation
 */

import type { ProjectData, NodeOperation } from '@/shared/types/projectData';

// Import basic operation handlers
import {
  handleCreate,
  handleUpdate,
  handleDelete,
  handleDeleteTree,
  handleMove,
  handleReorder,
  handleBatchDelete,
  handleBatchUpdate,
  handleProgressUpdate,
  handleGroupMove
} from './basicOperationHandlers';

// Import clipboard operation handlers
import {
  handleCopyNodes,
  handleCutNodes,
  handlePasteNodes,
  handleDuplicateNode,
  handleDuplicateSubtree
} from './clipboardOperationHandlers';

// Import file operation handlers
import {
  handleAttachFile,
  handleDetachFile,
  handleDeleteFile,
  handleRenameFile
} from './fileOperationHandlers';

// Import column operation handlers
import {
  handleAddColumn,
  handleUpdateColumn,
  handleDeleteColumn,
  handleReorderColumns
} from './columnOperationHandlers';

/**
 * Operation handler function type
 */
type OperationHandler = (data: ProjectData, op: NodeOperation) => ProjectData;

/**
 * Registry of operation handlers
 */
const operationHandlers: Record<string, OperationHandler> = {
  // Basic operations
  'create': handleCreate,
  'update': handleUpdate,
  'delete': handleDelete,
  'delete-tree': handleDeleteTree,
  'restore-tree': (data) => data,
  'move': handleMove,
  'reorder': handleReorder,
  'batch-delete': handleBatchDelete,
  'batch-update': handleBatchUpdate,
  'progress-update': handleProgressUpdate,
  'group-move': handleGroupMove,

  // Clipboard operations
  'copy-nodes': handleCopyNodes,
  'cut-nodes': handleCutNodes,
  'paste-nodes': handlePasteNodes,
  'duplicate-node': handleDuplicateNode,
  'duplicate-subtree': handleDuplicateSubtree,

  // File operations
  'attach-file': handleAttachFile,
  'detach-file': handleDetachFile,
  'delete-file': handleDeleteFile,
  'rename-file': handleRenameFile,

  // Column operations
  'add-column': handleAddColumn,
  'update-column': handleUpdateColumn,
  'delete-column': handleDeleteColumn,
  'reorder-columns': handleReorderColumns,

  // Special operations (no-op)
  'initial-load': (data, _op) => data // Initial load doesn't change data
};

/**
 * Apply an operation to project data
 * Replaces the massive switch statement with a clean registry lookup
 */
export function applyOperationViaRegistry(
  data: ProjectData,
  op: NodeOperation
): ProjectData {
  const handler = operationHandlers[op.type];

  if (handler) {
    return handler(data, op);
  }

  // Unknown operation type - return data unchanged
  console.warn(`Unknown operation type: ${op.type}`);
  return data;
}

/**
 * Check if an operation type is supported
 */
export function isOperationSupported(operationType: string): boolean {
  return operationType in operationHandlers;
}

/**
 * Get list of all supported operation types
 */
export function getSupportedOperations(): string[] {
  return Object.keys(operationHandlers);
}
